"use client";

import React, { useMemo } from "react";
import { useGame } from "@/app/providers/GameContextProvider";
import Image from "next/image";
import PlayerAvatar, { calculateDuplicateNumbers } from "@/app/components/PlayerAvatar";
import { useTranslation } from "@/hooks/useTranslation";
import { GameMode } from "@/lib/types/common/GameMode";
import { Team } from "@/lib/types/common/Team";
import { getTeamColorHex } from "@/lib/config/teams";

interface PlayerRankingData {
    sessionId: string;
    name: string;
    totalScore: number;
    avatarId: string;
    playerNumber: number;
    rank: number;
}

interface TeamRankingData {
    teamId: string;
    name: string;
    color: string;
    currentHp: number;
    maxHp: number;
    totalScore: number;
    isEliminated: boolean;
    rank: number;
}

interface FinalRankingsProps {
    isMobile?: boolean;
}

const FinalRankings = ({ isMobile = false }: FinalRankingsProps) => {
    const { gameState, room } = useGame();
    const { t } = useTranslation();

    // Use finalRankings if available (preserves rankings even when players leave)
    // Otherwise fallback to live players data (for compatibility during development)
    let playerRankings: PlayerRankingData[];

    if (gameState?.finalRankings && gameState?.finalRankings.length > 0) {
        // Use the snapshot created when game ended
        playerRankings = Array.from(gameState?.finalRankings).map(ranking => ({
            sessionId: ranking.sessionId,
            name: ranking.name,
            totalScore: ranking.totalScore,
            avatarId: ranking.avatarId,
            playerNumber: ranking.playerNumber || 0,
            rank: ranking.rank,
        }));
    } else {
        // Fallback to computing from live players data (old behavior)
        const sortedPlayers = Array.from(gameState?.players?.entries() || [])
            .map(([sessionId, player]) => ({
                sessionId,
                name: player.name,
                totalScore: player.totalScore,
                avatarId: player.avatarId,
                playerNumber: player.playerNumber || 0,
                rank: 0, // Will be set after sorting
            }))
            .sort((a, b) => b.totalScore - a.totalScore);

        // Handle ex aequo: assign same rank for same scores
        let currentRank = 1;
        let previousScore: number | null = null;
        playerRankings = sortedPlayers.map((player, index) => {
            if (previousScore !== null && player.totalScore !== previousScore) {
                currentRank = index + 1;
            }
            previousScore = player.totalScore;
            return { ...player, rank: currentRank };
        });
    }

    // Calculate duplicate numbers for avatars
    const duplicateNumbers = useMemo(() => {
        return calculateDuplicateNumbers(playerRankings.map(p => ({ sessionId: p.sessionId, avatarId: p.avatarId })));
    }, [playerRankings]);

    if (!gameState || !room) return null;

    // Use snapshot if available (preserves mode even after going back to lobby)
    const snapshotGameMode = gameState.gameSettingsSnapshot?.gameMode ?? gameState.gameMode;
    const snapshotBaseMode = gameState.gameSettingsSnapshot?.baseMode ?? gameState.gameSettings?.baseMode;

    const isFightMode = snapshotGameMode === GameMode.FIGHT ||
        (snapshotGameMode === GameMode.CUSTOM && snapshotBaseMode === GameMode.FIGHT);
    const isCoopMode = snapshotGameMode === GameMode.COOP ||
        (snapshotGameMode === GameMode.CUSTOM && snapshotBaseMode === GameMode.COOP);
    const isEndlessMode = snapshotGameMode === GameMode.ENDLESS ||
        (snapshotGameMode === GameMode.CUSTOM && snapshotBaseMode === GameMode.ENDLESS);
    const isTeamMode = isFightMode || isCoopMode;

    // For Fight mode or Coop mode: build team rankings
    // Use teamRankings snapshot if available (preserves rankings even when teams change)
    let teamRankings: TeamRankingData[] = [];
    if (isTeamMode) {
        if (gameState.teamRankings && gameState.teamRankings.length > 0) {
            // Use the snapshot created when game ended
            teamRankings = Array.from(gameState.teamRankings).map(ranking => ({
                teamId: ranking.teamId,
                name: ranking.name,
                color: ranking.color,
                currentHp: ranking.finalHp,
                maxHp: ranking.maxHp,
                totalScore: ranking.totalScore,
                isEliminated: ranking.isEliminated,
                rank: ranking.rank,
            }));
        } else if (isFightMode) {
            // Fallback: Fight mode - ordered by elimination (last alive = first place)
            const sortedFightTeams = Array.from(gameState.teams)
                .map((team: Team) => ({
                    teamId: team.id,
                    name: team.name,
                    color: team.color,
                    currentHp: team.currentHp,
                    maxHp: team.maxHp,
                    totalScore: team.totalScore,
                    isEliminated: team.isEliminated,
                    rank: 0,
                }))
                .sort((a, b) => {
                    // Alive teams come first
                    if (!a.isEliminated && b.isEliminated) return -1;
                    if (a.isEliminated && !b.isEliminated) return 1;
                    // Among alive teams, sort by HP descending
                    if (!a.isEliminated && !b.isEliminated) {
                        return b.currentHp - a.currentHp;
                    }
                    return 0;
                });

            // Handle ex aequo for Fight mode
            let fightCurrentRank = 1;
            let fightPreviousHp: number | null = null;
            let fightPreviousEliminated: boolean | null = null;
            teamRankings = sortedFightTeams.map((team, index) => {
                // Different rank if elimination status changed or HP changed (among non-eliminated)
                if (fightPreviousEliminated !== null &&
                    (team.isEliminated !== fightPreviousEliminated ||
                        (!team.isEliminated && team.currentHp !== fightPreviousHp))) {
                    fightCurrentRank = index + 1;
                }
                fightPreviousHp = team.currentHp;
                fightPreviousEliminated = team.isEliminated;
                return { ...team, rank: fightCurrentRank };
            });
        } else if (isCoopMode) {
            // Fallback: Coop mode - ordered by total score (highest = first place)
            const sortedCoopTeams = Array.from(gameState.teams)
                .map((team: Team) => ({
                    teamId: team.id,
                    name: team.name,
                    color: team.color,
                    currentHp: 0,
                    maxHp: 0,
                    totalScore: team.totalScore,
                    isEliminated: false,
                    rank: 0,
                }))
                .sort((a, b) => b.totalScore - a.totalScore);

            // Handle ex aequo for Coop mode
            let coopCurrentRank = 1;
            let coopPreviousScore: number | null = null;
            teamRankings = sortedCoopTeams.map((team, index) => {
                if (coopPreviousScore !== null && team.totalScore !== coopPreviousScore) {
                    coopCurrentRank = index + 1;
                }
                coopPreviousScore = team.totalScore;
                return { ...team, rank: coopCurrentRank };
            });
        }
    }



    return (
        <div className={`relative bg-gradient-to-b from-[#5E70BA] to-[#808ECA] rounded-xl border-4 border-[#313131] text-white shadow-[0px_0px_15px_5px_rgba(0,0,0,0.5),inset_0_0_2px_2px_rgba(0,0,0,0.8),inset_0_0_16px_#fff] ${isMobile ? 'w-full max-w-[95vw]' : 'max-w-[700px]'}`}>
            {/* vertical scanlines effect */}
            <div className="pointer-events-none absolute inset-0 bg-[repeating-linear-gradient(0deg,rgba(255,255,255,0.05),rgba(255,255,255,0.05)1px,rgba(0,0,0,0.05)4px)] rounded-xl"></div>

            <div className="mx-3 pt-2 text-3xl font-myriad-cond border-b-2 border-white/80 text-shadow shadow-indigo-900">
                {t("play.recap.results")}
            </div>

            <div className="py-[10px] px-6 text-shadow shadow-indigo-900">
                {/* Fight Mode or Coop Mode: Team Rankings */}
                {isTeamMode ? (
                    <div className={`flex ${isMobile ? 'gap-3' : 'gap-10'} justify-center`}>
                        {/* Team Ranks */}
                        {teamRankings.length >= 2 && (
                            <div className="flex flex-col">
                                <div className="font-myriad text-sm text-[#D1E8F7] text-center">{t("play.recap.rank")}</div>
                                <div className="flex flex-col gap-2">
                                    {teamRankings.map((team) => (
                                        <div key={team.teamId} className="flex items-center justify-center h-[50px]">
                                            {team.rank <= 3 && (
                                                <Image
                                                    src={`/ranks/rank_${team.rank}.png`}
                                                    alt={`Rank ${team.rank}`}
                                                    width={isMobile ? 35 : 50}
                                                    height={isMobile ? 35 : 50}
                                                />
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Team Names with Colored Square */}
                        <div className="flex flex-col">
                            <div className="font-myriad text-sm text-[#D1E8F7] text-center">{t("play.recap.name")}</div>
                            <div className="flex flex-col gap-2">
                                {teamRankings.map((team) => (
                                    <div key={team.teamId} className={`flex gap-2 items-center h-[50px] ${isMobile ? 'max-w-[150px]' : 'max-w-[330px]'}`}>
                                        <div
                                            className="w-[18px] h-[18px] rounded border border-yellow-300 shadow-[0_0_3px_1px_#000,inset_0_0_6px_#000] flex-shrink-0"
                                            style={{ backgroundColor: getTeamColorHex(team.color) }}
                                        ></div>
                                        <div className={`font-myriad ${isMobile ? 'text-base' : 'text-xl'} truncate ${isFightMode && team.isEliminated ? 'opacity-80 line-through' : ''}`}>
                                            {team.name}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Team HP (Fight mode) or Score (Coop mode) */}
                        <div className="flex flex-col">
                            <div className="font-myriad text-sm text-[#D1E8F7] text-center">
                                {isFightMode ? "HP" : isEndlessMode ? t("play.streak") : t("play.recap.score")}
                            </div>
                            <div className="flex flex-col gap-2">
                                {teamRankings.map((team) => (
                                    <div key={team.teamId} className="flex items-center justify-center h-[50px]">
                                        <div className={`font-myriad ${isMobile ? 'text-base' : 'text-xl'}`}>
                                            {isFightMode
                                                ? (team.isEliminated ? 0 : team.currentHp)
                                                : team.totalScore
                                            }
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                ) : (
                    /* Regular Mode: Player Rankings */
                    <div className={`flex ${isMobile ? 'gap-3' : 'gap-20'} justify-center`}>
                        {/* Player Final Rankings */}
                        {playerRankings.length >= 2 && (
                            <div className="flex flex-col">
                                <div className="font-myriad text-sm text-[#D1E8F7] text-center">{t("play.recap.rank")}</div>
                                <div className="flex flex-col gap-2">
                                    {playerRankings.map((player) => (
                                        <div key={player.sessionId} className="flex items-center justify-center h-[50px]">
                                            {player.rank <= 3 && (
                                                <Image
                                                    src={`/ranks/rank_${player.rank}.png`}
                                                    alt={`Rank ${player.rank}`}
                                                    width={isMobile ? 35 : 50}
                                                    height={isMobile ? 35 : 50}
                                                />
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Player Avatars and Names */}
                        <div className="flex flex-col">
                            <div className="font-myriad text-sm text-[#D1E8F7] text-center">{t("play.recap.name")}</div>
                            <div className="flex flex-col gap-2">
                                {playerRankings.map((player) => (
                                    <div key={player.sessionId} className={`flex gap-2 items-center h-[50px] ${isMobile ? 'max-w-[150px]' : 'max-w-[330px]'}`}>
                                        <div className={`${isMobile ? 'w-8 h-8' : 'w-10 h-10'} relative flex-shrink-0`}>
                                            <PlayerAvatar
                                                avatarId={player.avatarId}
                                                duplicateNumber={duplicateNumbers.get(player.sessionId) || 0}
                                                size={isMobile ? 32 : 40}
                                                className="w-full h-full"
                                                isLeader={gameState?.gameMasterId === player.sessionId}
                                                leaderIconSize={isMobile ? 18 : 24}
                                            />
                                        </div>
                                        <div className={`font-myriad ${isMobile ? 'text-base' : 'text-xl'} truncate`}>{player.name}</div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Player Scores */}
                        <div className="flex flex-col">
                            <div className="font-myriad text-sm text-[#D1E8F7] text-center">{t("play.recap.score")}</div>
                            <div className="flex flex-col gap-2">
                                {playerRankings.map((player) => (
                                    <div key={player.sessionId} className="flex items-center justify-center h-[50px]">
                                        <div className={`font-myriad ${isMobile ? 'text-base' : 'text-xl'}`}>{player.totalScore}</div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default FinalRankings;